import { useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShoppingCart, ArrowRight, Loader2, Check, Package } from 'lucide-react';
import { HTTPError } from 'ky';
import { type TurnstileInstance } from '@marsidev/react-turnstile';
import { useTheme } from '@evi/skins';
import { submitStoreLead } from '@/lib/api';
import { shopUrl } from '@/lib/shop';
import { useAuthStore } from '@/store/auth';
import { useCartStore, selectItemCount } from '@/store/cart';
import { TurnstileGate, TURNSTILE_SITE_KEY } from '@/components/ui/TurnstileGate';
import { EvieOrb } from '@/components/primitives';
import { useEvieChatStore } from '@/store/evieChat';
import styles from './Store.module.css';

/**
 * /store/why/:icp — one page per ICP (homeowner, property manager,
 * electrician), reached from the catalog's customer-voices row. Each
 * page: the matching UGC film as the hero (same heroBg/heroScrim
 * chrome as the catalog, video instead of the still), founder copy,
 * two quote cards, and a CTA back into the catalog pre-filtered to
 * the right tier (?tier= — StoreCatalogPage reads it on land).
 *
 * The electrician page adds the Sales Partner section with the email
 * capture that feeds POST /store/leads (source sp_electrician_page).
 */

interface IcpQuote { text: string; name: string }
interface IcpContent {
  video: string;
  poster: string;
  title: string;
  body: string;
  quotes: [IcpQuote, IcpQuote];
  ctaLabel: string;
  ctaTier: 'home' | 'commercial_ac';
  /** When set, the CTA is an EXTERNAL link to the public shop instead of an
   *  in-app tier navigation (the electrician ICP points at evinitiative.shop
   *  per Warren's retarget decision). */
  ctaHref?: string;
  salesPartner?: boolean;
}

const ICP_CONTENT: Record<string, IcpContent> = {
  homeowner: {
    video: '/media/ugc/homeowner.mp4',
    poster: '/media/ugc/homeowner-poster.jpg',
    title: 'Charge at home. Wake up full.',
    body:
      'Your garage becomes your gas station. The EVI app starts your charge, tracks '
      + 'every kilowatt hour, and shows you exactly what it costs. Add your family to '
      + 'your free pass list and they charge free too. Buy the charger, plug in, and '
      + 'the app walks you through setup in minutes.',
    quotes: [
      {
        text: 'I plug in at night and stop thinking about it. The app shows me every '
          + 'session and my husband charges on my free pass list for nothing.',
        name: 'Sienna R., homeowner, Sacramento CA',
      },
      {
        text: 'Setup took one evening. The charger showed up, my electrician mounted '
          + 'it, and the app found it on the first try.',
        name: 'Dana M., homeowner, Mesa AZ',
      },
    ],
    ctaLabel: 'Shop home chargers',
    ctaTier: 'home',
  },
  'property-manager': {
    video: '/media/ugc/property-manager.mp4',
    poster: '/media/ugc/property-manager-poster.jpg',
    title: 'Amenity income, not amenity overhead.',
    body:
      'Your residents want charging. EVI makes it pay. Set your rate, and the '
      + 'platform bills drivers, pays you out through Stripe, and hands you the '
      + 'reports your owner wants to see. No attendant, no billing spreadsheet, no '
      + 'service calls routed through your office. Residents charge with the app, '
      + 'guests pay your markup, and you watch it all from your phone.',
    quotes: [
      {
        text: 'Two pedestals in the visitor lot went from a cost line to a revenue '
          + 'line. The payout hits our account and the report goes straight to ownership.',
        name: 'Sterling J., property manager, Atlanta GA',
      },
      {
        text: 'Residents stopped emailing me about charging. The app handles access, '
          + 'billing, and the one dispute we had came with the session record attached.',
        name: 'Priya K., community manager, Charlotte NC',
      },
    ],
    ctaLabel: 'Shop commercial chargers',
    ctaTier: 'commercial_ac',
    // Property-manager retarget: the public shop's Commercial AC page
    // (multifamily / long-term-parking AC charging).
    ctaHref: shopUrl('/commercial-ac'),
  },
  electrician: {
    video: '/media/ugc/electrician.mp4',
    poster: '/media/ugc/electrician-poster.jpg',
    title: 'Install it once. Earn on it forever.',
    body:
      'Every charger you install on EVI keeps working for you. Commission the '
      + 'hardware, hand the customer an app that runs itself, and stop fielding '
      + 'support calls for free. Clean OCPP hardware, drop-ship delivery to the job '
      + 'site, and a network that makes you look good on every install.',
    quotes: [
      {
        text: 'I quoted the install, EVI drop-shipped the unit to the site, and it was '
          + "on the network the same day. The customer thinks I'm a wizard.",
        name: 'Brooks T., licensed electrician, Tampa FL',
      },
      {
        text: 'The partner commission turned my install business into recurring '
          + 'income. Every unit I put on a wall keeps paying.',
        name: 'Marco V., EV installer, Orlando FL',
      },
    ],
    ctaLabel: 'Shop commercial chargers',
    ctaTier: 'commercial_ac',
    // Electrician retarget: send them to the public shop's Commercial DC page.
    ctaHref: shopUrl('/commercial-dc'),
    salesPartner: true,
  },
};

interface Props { icp: string; onNavigate: (path: string) => void }

export function StoreWhyPage({ icp, onNavigate }: Props) {
  const { t } = useTranslation('storeCheckout');
  const { mode } = useTheme();
  const itemCount = useCartStore(selectItemCount);
  const content = ICP_CONTENT[icp];
  if (!content) {
    // Unknown slug (route enum should prevent this): back to the catalog.
    onNavigate('/store');
    return null;
  }
  return (
    <div className={styles.store} data-store-theme={mode}>
      <header className={`${styles.head} ${styles.headSolid}`}>
        <button type="button" className={styles.headBtn} aria-label={t('why.back')} onClick={() => onNavigate('/store')}>
          <ArrowLeft size={18} />
        </button>
        <span className={`${styles.headTitle} ${styles.headTitleShown}`}>{t('why.title')}</span>
        <span className={styles.headActions}>
          <button
            type="button"
            className={styles.headBtn}
            aria-label={t('why.askEvii')}
            onClick={() => {
              useEvieChatStore.getState().setContext({ surface: 'store' });
              useEvieChatStore.getState().openSheet();
            }}
          >
            <EvieOrb size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('why.myOrders')} onClick={() => onNavigate('/store/orders')}>
            <Package size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('why.cart')} onClick={() => onNavigate('/store/cart')}>
            <ShoppingCart size={18} />
            {itemCount > 0 && <span className={styles.headCartCount}>{itemCount}</span>}
          </button>
        </span>
      </header>

      {/* Hero — the ICP's UGC film behind the catalog's scrim chrome.
          data-icp keys the per-page desktop crop (Store.module.css): the
          subject sits at a different height in each UGC film. */}
      <section className={`${styles.hero} ${styles.whyHero}`} data-icp={icp}>
        <video
          className={styles.heroBg}
          src={content.video}
          poster={content.poster}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className={styles.heroScrim} aria-hidden />
        <div className={styles.heroContent}>
          <span className={styles.heroEyebrow}>EV INITIATIVE</span>
          <h1 className={styles.heroTitle}>{t(`icp.${icp}.title`, { defaultValue: content.title })}</h1>
        </div>
      </section>

      <div className={styles.body}>
        <p className={styles.whyBody}>{t(`icp.${icp}.body`, { defaultValue: content.body })}</p>

        {content.quotes.map((q) => (
          <figure key={q.name} className={styles.whyQuote}>
            <blockquote className={styles.whyQuoteText}>&ldquo;{q.text}&rdquo;</blockquote>
            <figcaption className={styles.whyQuoteName}>{q.name}</figcaption>
          </figure>
        ))}

        {content.salesPartner && <SalesPartnerSection />}

        {content.ctaHref ? (
          <a
            className={styles.sheetAdd}
            href={content.ctaHref}
            target="_blank"
            rel="noreferrer"
          >
            {t(`icp.${icp}.ctaLabel`, { defaultValue: content.ctaLabel })} <ArrowRight size={16} />
          </a>
        ) : (
          <button
            type="button"
            className={styles.sheetAdd}
            onClick={() => onNavigate(`/store?tier=${content.ctaTier}`)}
          >
            {t(`icp.${icp}.ctaLabel`, { defaultValue: content.ctaLabel })} <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Sales Partner capture (electrician page only) ─────────────────────
// Inline email capture feeding POST /store/leads. Prefilled with the
// signed-in driver's email but editable; neutral thank-you state after
// submit. The full SP lander comes later — no redirect yet.
function SalesPartnerSection() {
  const { t } = useTranslation('storeCheckout');
  const userEmail = useAuthStore((s) => s.user?.email ?? '');
  const [phase, setPhase] = useState<'idle' | 'form' | 'done'>('idle');
  const [email, setEmail] = useState(userEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Turnstile gate: the token blocks submit only when a site key is
  // baked into the build; keyless builds (local dev, keyless staging)
  // submit without one and the server's fail-open policy accepts.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || busy) return;
    if (TURNSTILE_SITE_KEY && !turnstileToken) return;
    setBusy(true);
    setError(null);
    try {
      await submitStoreLead(trimmed, 'sp_electrician_page', turnstileToken ?? undefined);
      setPhase('done');
    } catch (err) {
      // A server-side captcha rejection means the stale token is
      // spent — reset the widget so a fresh challenge can complete
      // instead of resubmitting a dead token.
      if (err instanceof HTTPError) {
        try {
          const body = await err.response.clone().json() as { error?: { code?: string } };
          if (body?.error?.code === 'TURNSTILE_FAILED' || body?.error?.code === 'TURNSTILE_REQUIRED') {
            turnstileRef.current?.reset();
            setTurnstileToken(null);
          }
        } catch {
          // Body wasn't JSON — nothing captcha-specific to reset.
        }
      }
      setError(t('sp.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={styles.spCard}>
      <h2 className={styles.spHeading}>{t('sp.heading')}</h2>
      <p className={styles.spLine}>
        {t('sp.tagline')}
      </p>
      <p className={styles.spBody}>
        {t('sp.body')}
      </p>

      {phase === 'idle' && (
        <button type="button" className={styles.sheetAdd} onClick={() => setPhase('form')}>
          {t('sp.learnMore')}
        </button>
      )}

      {phase === 'form' && (
        <form className={styles.leadForm} onSubmit={handleSubmit}>
          <p className={styles.spBody}>
            {t('sp.formPrompt')}
          </p>
          <input
            type="email"
            className={`${styles.leadInput} clarity-mask`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('sp.emailPlaceholder')}
            required
            autoComplete="email"
          />
          <TurnstileGate
            ref={turnstileRef}
            posture="open"
            className={styles.turnstileWrap}
            onToken={setTurnstileToken}
          />
          <button
            type="submit"
            className={styles.sheetAdd}
            disabled={busy || Boolean(TURNSTILE_SITE_KEY && !turnstileToken)}
          >
            {busy ? <Loader2 size={16} className={styles.checkoutSpin} /> : t('sp.submit')}
          </button>
          {error && <p className={styles.leadError}>{error}</p>}
        </form>
      )}

      {phase === 'done' && (
        <p className={styles.leadThanks}>
          <Check size={15} /> {t('sp.done')}
        </p>
      )}
    </section>
  );
}
